import { useState, useEffect, useCallback, useRef } from "react";

// ── Supabase ──────────────────────────────────────────────────────────────────
const SB_URL = "https://lqnfkimcoqzlbegyuxnj.supabase.co";
const SB_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxxbmZraW1jb3F6bGJlZ3l1eG5qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODUxNDI3MDEsImV4cCI6MjEwMDcxODcwMX0.aPeKRH_D6v8JDpzCaaji8GIeV2s06qxfIwdj-OJDndE";

const sb = async (path, method = "GET", body = null) => {
  const res = await fetch(`${SB_URL}/rest/v1/${path}`, {
    method,
    headers: {
      apikey: SB_KEY,
      Authorization: `Bearer ${SB_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    body: body ? JSON.stringify(body) : null,
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text);
  return text ? JSON.parse(text) : [];
};

// ── Constants ─────────────────────────────────────────────────────────────────
const C = {
  accent: "#6C63FF", success: "#22C55E", warning: "#F59E0B",
  danger: "#EF4444", info: "#38BDF8", purple: "#A78BFA",
};

const STATUS_FLOW = ["Not Started", "Started", "In Progress", "Sent", "Completed"];
const STATUS_COLOR = {
  "Not Started": "#6B7280", "Started": "#38BDF8",
  "In Progress": "#F59E0B", "Sent": "#A78BFA", "Completed": "#22C55E",
};

const CATEGORIES = ["Reel","Logo","Poster","Website","Visiting Card","Video Edit","Photo Edit","Content","Other"];
const TAGS = ["Urgent","VIP","Monthly","One Time","Repeat Client","High Priority"];
const PRIORITIES = ["Low","Medium","High"];
const PRIORITY_COLOR = { Low: "#22C55E", Medium: "#F59E0B", High: "#EF4444" };

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) : "";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "";
const todayStr = () => new Date().toDateString();
const isOverdue = (deadline, status) => deadline && status !== "Completed" && new Date(deadline) < new Date();

// ── Theme ─────────────────────────────────────────────────────────────────────
const DARK  = { bg:"#0D0F1A", surface:"#161929", card:"#1E2236", text:"#F0F0F8", muted:"#6B7280", border:"#252A40", input:"#13162A" };
const LIGHT = { bg:"#F4F5FB", surface:"#FFFFFF", card:"#ECEEF8", text:"#111827", muted:"#6B7280", border:"#D1D5DB", input:"#F9FAFB" };

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [dark, setDark] = useState(true);
  const T = dark ? DARK : LIGHT;

  const [clients, setClients]   = useState([]);
  const [works, setWorks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState("dashboard");
  const [activeClient, setActiveClient] = useState(null);
  const [activeWork, setActiveWork]     = useState(null);
  const [searchQ, setSearchQ]   = useState("");
  const [toast, setToast]       = useState(null);
  const [modal, setModal]       = useState(null); // "add-client" | "add-work" | "report"

  const notify = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),2800); };

  // ── Load ───────────────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [cls, wks, revs, progs, tls] = await Promise.all([
        sb("clients?order=created_at.desc"),
        sb("works?order=created_at.desc"),
        sb("revisions?order=created_at.asc"),
        sb("progress?order=created_at.asc"),
        sb("timeline?order=created_at.asc"),
      ]);
      setClients(cls);
      setWorks(wks.map(w => ({
        ...w,
        tags: w.tags || [],
        revisions: revs.filter(r => r.work_id === w.id),
        progress:  progs.filter(p => p.work_id === w.id),
        timeline:  tls.filter(t => t.work_id === w.id),
      })));
    } catch(e) { notify("Load error: "+e.message,"error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Sync helper ────────────────────────────────────────────────────────────
  const syncWork = (updated) => {
    setWorks(p => p.map(w => w.id===updated.id ? updated : w));
    if (activeWork?.id===updated.id) setActiveWork(updated);
  };

  // ── Client CRUD ────────────────────────────────────────────────────────────
  const addClient = async (data) => {
    const [c] = await sb("clients","POST",data);
    setClients(p=>[{...c},...p]);
    notify("Client added! 👤");
  };

  // ── Work CRUD ──────────────────────────────────────────────────────────────
  const addWork = async (data) => {
    const [w] = await sb("works","POST",{...data, budget:+data.budget||0, advance:+data.advance||0});
    await sb("timeline","POST",{work_id:w.id, event:"Created"});
    const enriched = {...w, tags:w.tags||[], revisions:[], progress:[], timeline:[{event:"Created",created_at:new Date().toISOString()}]};
    setWorks(p=>[enriched,...p]);
    notify("Work created! 🎯");
  };

  // ── Status Advance ─────────────────────────────────────────────────────────
  const advanceStatus = async (work) => {
    const idx = STATUS_FLOW.indexOf(work.status);
    if (idx>=STATUS_FLOW.length-1) return;
    const next = STATUS_FLOW[idx+1];
    await sb(`works?id=eq.${work.id}`,"PATCH",{status:next});
    const tl = [...work.timeline, {event:next, created_at:new Date().toISOString()}];
    await sb("timeline","POST",{work_id:work.id, event:next});
    syncWork({...work, status:next, timeline:tl});
    notify(`→ ${next}`);
  };

  // ── Revision ───────────────────────────────────────────────────────────────
  const addRevision = async (work, note) => {
    const [rev] = await sb("revisions","POST",{work_id:work.id, note});
    const num = work.revisions.length+1;
    await sb("timeline","POST",{work_id:work.id, event:`Revision ${num}`});
    const tl = [...work.timeline,{event:`Revision ${num}`,created_at:new Date().toISOString()}];
    syncWork({...work, revisions:[...work.revisions,rev], timeline:tl});
    notify(`Revision ${num} added!`);
  };

  // ── Progress ───────────────────────────────────────────────────────────────
  const addProgress = async (work, line) => {
    const [prog] = await sb("progress","POST",{work_id:work.id, line});
    syncWork({...work, progress:[...work.progress, prog]});
    notify("Progress saved!");
  };

  // ── Delivery ───────────────────────────────────────────────────────────────
  const toggleDelivered = async (work) => {
    const val = !work.delivered;
    await sb(`works?id=eq.${work.id}`,"PATCH",{delivered:val});
    let tl = work.timeline;
    if (val) { await sb("timeline","POST",{work_id:work.id,event:"Delivered"}); tl=[...tl,{event:"Delivered",created_at:new Date().toISOString()}]; }
    syncWork({...work, delivered:val, timeline:tl});
    notify(val ? "✓ Delivered!" : "Delivery undone");
  };

  // ── Payment ────────────────────────────────────────────────────────────────
  const setPaymentState = async (work, state) => {
    // state: "none" | "advance" | "full"
    const advance_received = state==="advance"||state==="full";
    const paid = state==="full";
    await sb(`works?id=eq.${work.id}`,"PATCH",{advance_received, paid});
    let tl = work.timeline;
    if (state==="advance" && !work.advance_received) { await sb("timeline","POST",{work_id:work.id,event:"Advance Received"}); tl=[...tl,{event:"Advance Received",created_at:new Date().toISOString()}]; }
    if (state==="full" && !work.paid) { await sb("timeline","POST",{work_id:work.id,event:"Full Payment Received"}); tl=[...tl,{event:"Full Payment Received",created_at:new Date().toISOString()}]; }
    syncWork({...work, advance_received, paid, timeline:tl});
    notify(state==="full"?"💰 Full Payment Done!":state==="advance"?"Advance Received!":"Payment reset");
  };

  // ── Notes ──────────────────────────────────────────────────────────────────
  const saveNotes = async (work, notes) => {
    await sb(`works?id=eq.${work.id}`,"PATCH",{notes});
    syncWork({...work, notes});
  };

  // ── Computed ───────────────────────────────────────────────────────────────
  const todayWorks    = works.filter(w => new Date(w.created_at).toDateString()===todayStr());
  const pendingWorks  = works.filter(w => w.status!=="Completed");
  const completedWorks= works.filter(w => w.status==="Completed");
  const overdueWorks  = works.filter(w => isOverdue(w.deadline, w.status));
  const todayRevisions= works.filter(w => w.revisions.some(r=>new Date(r.created_at).toDateString()===todayStr()));
  const unpaidWorks   = works.filter(w => !w.paid && w.status==="Completed");
  const clientWorks   = (cid) => works.filter(w=>w.client_id===cid);

  // Revenue
  const totalBudget   = works.reduce((a,w)=>a+(w.budget||0),0);
  const totalReceived = works.reduce((a,w)=>a+(w.paid?(w.budget||0):w.advance_received?(w.advance||0):0),0);
  const totalPending  = totalBudget - totalReceived;

  // Search
  const searchResults = searchQ.length>1 ? {
    clients: clients.filter(c=>`${c.name} ${c.company||""}`.toLowerCase().includes(searchQ.toLowerCase())),
    works:   works.filter(w=>`${w.title} ${w.category} ${w.status} ${w.tags?.join(" ")||""}`.toLowerCase().includes(searchQ.toLowerCase())),
  } : null;

  // Nav
  const openClient=(c)=>{setActiveClient(c);setView("client-detail");};
  const openWork=(w)=>{setActiveWork(w);setView("work-detail");};
  const goBack=()=>{
    if(view==="work-detail"){setView("client-detail");setActiveWork(null);}
    else{setView("clients");setActiveClient(null);}
  };

  const s = mkStyles(T);

  if (loading) return <Loader />;

  return (
    <div style={s.shell}>
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      {/* HEADER */}
      <header style={s.header}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <span style={s.logo}>◈ Workflow</span>
          {(view==="client-detail"||view==="work-detail") &&
            <button style={s.backBtn} onClick={goBack}>← Back</button>}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={s.searchWrap}>
            <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:14}}>🔍</span>
            <input style={s.searchInput} placeholder="Search clients, work, tags…" value={searchQ}
              onChange={e=>{setSearchQ(e.target.value);if(e.target.value)setView("search");else setView("dashboard");}} />
          </div>
          <button style={s.iconBtn} onClick={()=>setDark(d=>!d)} title="Toggle theme">{dark?"☀️":"🌙"}</button>
        </div>
      </header>

      {/* NAV */}
      <nav style={s.nav}>
        {[["dashboard","📊 Dashboard"],["clients","👥 Clients"]].map(([k,l])=>(
          <button key={k} style={{...s.navBtn,...(view===k||view==="client-detail"||view==="work-detail"&&k==="clients"?s.navActive:{})}}
            onClick={()=>{setView(k);setSearchQ("");}}>
            {l}
          </button>
        ))}
      </nav>

      {/* MAIN */}
      <main style={s.main}>

        {/* ── SEARCH ── */}
        {view==="search" && (
          <div>
            <h2 style={s.pageTitle}>Search: "{searchQ}"</h2>
            {searchResults?.clients.map(c=>(
              <div key={c.id} style={s.listCard} onClick={()=>openClient(c)}>
                <Avatar name={c.name} size={38} /><div><div style={{color:T.text,fontWeight:700}}>{c.name}</div><div style={{color:T.muted,fontSize:12}}>{c.company}</div></div>
              </div>
            ))}
            {searchResults?.works.map(w=>(
              <div key={w.id} style={s.listCard} onClick={()=>openWork(w)}>
                <StatusDot status={w.status}/><div style={{flex:1}}><div style={{color:T.text,fontWeight:700}}>{w.title}</div><div style={{color:T.muted,fontSize:12}}>{w.category}</div></div>
                <StatusBadge status={w.status}/>
              </div>
            ))}
            {!searchResults?.clients.length && !searchResults?.works.length &&
              <EmptyState icon="🔍" msg="No results found" />}
          </div>
        )}

        {/* ── DASHBOARD ── */}
        {view==="dashboard" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h2 style={s.pageTitle}>Dashboard</h2>
              <span style={{color:T.muted,fontSize:13}}>{new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long"})}</span>
            </div>

            {/* Overdue Alert */}
            {overdueWorks.length>0 && (
              <div style={{background:C.danger+"18",border:`1px solid ${C.danger}44`,borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
                <span style={{fontSize:20}}>🚨</span>
                <div>
                  <div style={{color:C.danger,fontWeight:700,fontSize:14}}>{overdueWorks.length} Overdue Work{overdueWorks.length>1?"s":""}</div>
                  <div style={{color:T.muted,fontSize:12}}>{overdueWorks.map(w=>w.title).join(", ")}</div>
                </div>
              </div>
            )}

            {/* Stats */}
            <div style={s.statsGrid}>
              <StatCard label="Today's Work" value={todayWorks.length} color={C.accent} icon="📋" T={T} onClick={()=>{}} />
              <StatCard label="Pending" value={pendingWorks.length} color={C.warning} icon="⏳" T={T} />
              <StatCard label="Completed" value={completedWorks.length} color={C.success} icon="✅" T={T} />
              <StatCard label="Today's Revisions" value={todayRevisions.length} color={C.purple} icon="🔄" T={T} />
              <StatCard label="Pending Payments" value={unpaidWorks.length} color={C.danger} icon="💳" T={T} />
              <StatCard label="Overdue" value={overdueWorks.length} color={C.danger} icon="🚨" T={T} />
            </div>

            {/* Revenue */}
            <div style={{...s.panel,marginBottom:16}}>
              <div style={s.panelTitle}>💰 Revenue Summary</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
                <div style={{textAlign:"center"}}>
                  <div style={{color:C.accent,fontWeight:800,fontSize:22}}>₹{totalBudget.toLocaleString()}</div>
                  <div style={{color:T.muted,fontSize:11,marginTop:2}}>Total Budget</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{color:C.success,fontWeight:800,fontSize:22}}>₹{totalReceived.toLocaleString()}</div>
                  <div style={{color:T.muted,fontSize:11,marginTop:2}}>Received</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{color:C.danger,fontWeight:800,fontSize:22}}>₹{totalPending.toLocaleString()}</div>
                  <div style={{color:T.muted,fontSize:11,marginTop:2}}>Pending</div>
                </div>
              </div>
            </div>

            <div style={s.grid2}>
              {/* Recent Activity */}
              <div style={s.panel}>
                <div style={s.panelTitle}>⚡ Recent Activity</div>
                {works.slice(0,8).flatMap(w=>
                  w.timeline.slice(-2).map((t,i)=>(
                    <div key={w.id+i} style={{display:"flex",gap:8,padding:"5px 0",borderBottom:`1px solid ${T.border}`,alignItems:"center"}}>
                      <span style={{color:C.accent,fontSize:11,minWidth:90,flexShrink:0}}>{fmt(t.created_at)}</span>
                      <span style={{color:T.muted,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><b style={{color:T.text}}>{w.title}</b> → {t.event}</span>
                    </div>
                  ))
                ).slice(0,10)}
              </div>

              {/* Pending Payments */}
              <div style={s.panel}>
                <div style={s.panelTitle}>💳 Pending Payments</div>
                {unpaidWorks.length===0
                  ? <EmptyState icon="🎉" msg="All payments cleared!" small />
                  : unpaidWorks.map(w=>{
                    const cl=clients.find(c=>c.id===w.client_id);
                    return (
                      <div key={w.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${T.border}`,cursor:"pointer"}} onClick={()=>openWork(w)}>
                        <div>
                          <div style={{color:T.text,fontWeight:600,fontSize:13}}>{w.title}</div>
                          <div style={{color:T.muted,fontSize:11}}>{cl?.name}</div>
                        </div>
                        <div style={{color:C.danger,fontWeight:800,fontSize:14}}>₹{((w.budget||0)-(w.advance||0)).toLocaleString()}</div>
                      </div>
                    );
                  })
                }
              </div>
            </div>

            {/* Today's Work List */}
            {todayWorks.length>0 && (
              <div style={{...s.panel,marginTop:16}}>
                <div style={s.panelTitle}>📋 Today's Work</div>
                {todayWorks.map(w=>(
                  <div key={w.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${T.border}`,cursor:"pointer"}} onClick={()=>openWork(w)}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <StatusDot status={w.status}/>
                      <div>
                        <div style={{color:T.text,fontWeight:600,fontSize:13}}>{w.title}</div>
                        <div style={{color:T.muted,fontSize:11}}>{w.category}</div>
                      </div>
                    </div>
                    <StatusBadge status={w.status}/>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CLIENTS ── */}
        {view==="clients" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h2 style={s.pageTitle}>Clients</h2>
              <button style={s.primaryBtn} onClick={()=>setModal("add-client")}>+ Add Client</button>
            </div>
            {clients.length===0 && <EmptyState icon="👤" msg="No clients yet. Add your first client!" />}
            {clients.map(c=>{
              const cw=clientWorks(c.id);
              const pending=cw.filter(w=>w.status!=="Completed").length;
              return (
                <div key={c.id} style={s.clientCard} onClick={()=>openClient(c)}>
                  <Avatar name={c.name} size={46}/>
                  <div style={{flex:1}}>
                    <div style={{color:T.text,fontWeight:700,fontSize:15}}>{c.name}</div>
                    {c.company&&<div style={{color:T.muted,fontSize:12}}>{c.company}</div>}
                    <div style={{color:T.muted,fontSize:12}}>{c.phone}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{color:C.accent,fontWeight:800,fontSize:16}}>{cw.length}</div>
                    <div style={{color:T.muted,fontSize:11}}>works</div>
                    {pending>0&&<div style={{color:C.warning,fontSize:11}}>{pending} pending</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── CLIENT DETAIL ── */}
        {view==="client-detail" && activeClient && (
          <div>
            {/* Client Info */}
            <div style={{...s.panel,marginBottom:20,display:"flex",gap:16,alignItems:"flex-start"}}>
              <Avatar name={activeClient.name} size={56}/>
              <div style={{flex:1}}>
                <div style={{color:T.text,fontWeight:800,fontSize:20}}>{activeClient.name}</div>
                {activeClient.company&&<div style={{color:T.muted,fontSize:13}}>{activeClient.company}</div>}
                {activeClient.phone&&<div style={{color:T.muted,fontSize:13}}>📱 {activeClient.phone}</div>}
                {activeClient.email&&<div style={{color:T.muted,fontSize:13}}>✉️ {activeClient.email}</div>}
                {activeClient.notes&&<div style={{color:C.warning,fontSize:13,marginTop:6}}>📝 {activeClient.notes}</div>}
              </div>
              <button style={s.primaryBtn} onClick={()=>setModal("report")}>📄 Report</button>
            </div>

            {/* Works */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={s.panelTitle}>Works ({clientWorks(activeClient.id).length})</div>
              <button style={s.primaryBtn} onClick={()=>setModal("add-work")}>+ Add Work</button>
            </div>
            {clientWorks(activeClient.id).length===0 && <EmptyState icon="🎯" msg="No works yet" />}
            {clientWorks(activeClient.id).map(w=>(
              <div key={w.id} style={{...s.listCard,flexDirection:"column",alignItems:"stretch",gap:10}} onClick={()=>openWork(w)}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <StatusDot status={w.status}/>
                    <div>
                      <div style={{color:T.text,fontWeight:700}}>{w.title}</div>
                      <div style={{color:T.muted,fontSize:12}}>{w.category}{w.deadline?` · Due ${fmtDate(w.deadline)}`:""}</div>
                    </div>
                  </div>
                  <StatusBadge status={w.status}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",gap:6}}>
                    <PriorityBadge priority={w.priority}/>
                    {w.tags?.slice(0,2).map(t=><span key={t} style={s.tag}>{t}</span>)}
                    {isOverdue(w.deadline,w.status)&&<span style={{...s.tag,background:C.danger+"22",color:C.danger,border:`1px solid ${C.danger}44`}}>Overdue</span>}
                  </div>
                  <span style={{color:w.paid?C.success:C.danger,fontSize:12,fontWeight:600}}>
                    ₹{(w.budget||0).toLocaleString()} {w.paid?"✓":"⏳"}
                  </span>
                </div>
                {w.revisions.length>0&&<div style={{color:T.muted,fontSize:11}}>🔄 {w.revisions.length} revision{w.revisions.length>1?"s":""}</div>}
              </div>
            ))}
          </div>
        )}

        {/* ── WORK DETAIL ── */}
        {view==="work-detail" && activeWork && (
          <WorkDetail
            work={activeWork} T={T} s={s}
            onAdvance={()=>advanceStatus(activeWork)}
            onRevision={note=>addRevision(activeWork,note)}
            onProgress={line=>addProgress(activeWork,line)}
            onDelivery={()=>toggleDelivered(activeWork)}
            onPayment={state=>setPaymentState(activeWork,state)}
            onNoteChange={notes=>saveNotes(activeWork,notes)}
          />
        )}
      </main>

      {/* ── MODALS ── */}
      {modal==="add-client" && (
        <AddClientModal T={T} s={s}
          onSave={async d=>{await addClient(d);setModal(null);}}
          onClose={()=>setModal(null)} />
      )}
      {modal==="add-work" && activeClient && (
        <AddWorkModal T={T} s={s} clientId={activeClient.id}
          onSave={async d=>{await addWork(d);setModal(null);}}
          onClose={()=>setModal(null)} />
      )}
      {modal==="report" && activeClient && (
        <ReportModal T={T} s={s} client={activeClient} works={clientWorks(activeClient.id)} onClose={()=>setModal(null)} />
      )}
    </div>
  );
}

// ── Work Detail ───────────────────────────────────────────────────────────────
function WorkDetail({ work, T, s, onAdvance, onRevision, onProgress, onDelivery, onPayment, onNoteChange }) {
  const [revNote, setRevNote] = useState("");
  const [progLine, setProgLine] = useState("");
  const [noteText, setNoteText] = useState(work.notes||"");
  const noteTimer = useRef(null);
  const idx = STATUS_FLOW.indexOf(work.status);

  const handleNoteChange = (v) => {
    setNoteText(v);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(()=>onNoteChange(v), 800);
  };

  return (
    <div>
      {/* Title */}
      <div style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
          <div>
            <h2 style={{...s.pageTitle,marginBottom:4}}>{work.title}</h2>
            <div style={{color:T.muted,fontSize:13,display:"flex",gap:12}}>
              <span>{work.category}</span>
              {work.deadline&&<span style={{color:isOverdue(work.deadline,work.status)?C.danger:T.muted}}>📅 {fmtDate(work.deadline)}{isOverdue(work.deadline,work.status)?" 🚨":""}</span>}
            </div>
          </div>
          <PriorityBadge priority={work.priority} large/>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:10}}>
          {work.tags?.map(t=><span key={t} style={s.tag}>{t}</span>)}
        </div>
      </div>

      {/* Status Flow */}
      <div style={s.panel}>
        <div style={s.panelTitle}>Status</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
          {STATUS_FLOW.map((st,i)=>(
            <div key={st} style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{background:work.status===st?STATUS_COLOR[st]:T.card,color:work.status===st?"#fff":T.muted,border:`1.5px solid ${work.status===st?STATUS_COLOR[st]:T.border}`,borderRadius:20,padding:"4px 12px",fontSize:12,fontWeight:700,whiteSpace:"nowrap"}}>
                {i<=idx?"✓ ":""}{st}
              </div>
              {i<STATUS_FLOW.length-1&&<span style={{color:T.border}}>→</span>}
            </div>
          ))}
        </div>
        {work.status!=="Completed"&&(
          <button style={{...s.primaryBtn,background:STATUS_COLOR[STATUS_FLOW[idx+1]]||C.accent,width:"100%"}} onClick={onAdvance}>
            → Move to {STATUS_FLOW[idx+1]}
          </button>
        )}
        {work.status==="Completed"&&<div style={{color:C.success,fontWeight:700,textAlign:"center",padding:8}}>✅ Work Complete!</div>}
      </div>

      {/* Delivery & Payment */}
      <div style={s.panel}>
        <div style={s.panelTitle}>Delivery & Payment</div>

        {/* Delivery */}
        <div style={{marginBottom:14}}>
          <div style={{color:T.muted,fontSize:12,marginBottom:8}}>Delivery</div>
          <button style={{...s.toggleBtn,background:work.delivered?C.success:T.card,color:work.delivered?"#fff":T.muted,border:`1.5px solid ${work.delivered?C.success:T.border}`,width:"100%"}} onClick={onDelivery}>
            {work.delivered?"✓ Delivered — Click to undo":"○ Mark as Delivered"}
          </button>
        </div>

        {/* Payment Buttons */}
        <div style={{color:T.muted,fontSize:12,marginBottom:8}}>Payment</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
          <button
            style={{...s.toggleBtn,background:work.advance_received&&!work.paid?C.warning:work.paid?C.success+"33":T.card,color:work.advance_received?"#fff":T.muted,border:`1.5px solid ${work.advance_received?work.paid?C.success:C.warning:T.border}`}}
            onClick={()=>onPayment(work.advance_received?"none":"advance")}>
            {work.advance_received?"✓ Advance Received":"○ Advance Received"}
          </button>
          <button
            style={{...s.toggleBtn,background:work.paid?C.success:T.card,color:work.paid?"#fff":T.muted,border:`1.5px solid ${work.paid?C.success:T.border}`}}
            onClick={()=>onPayment(work.paid?"advance":"full")}>
            {work.paid?"✓ Full Payment Done":"○ Full Payment Received"}
          </button>
        </div>

        {/* Payment Summary */}
        <div style={{background:T.bg,borderRadius:10,padding:"10px 14px"}}>
          <PayRow label="Budget" value={`₹${(work.budget||0).toLocaleString()}`} T={T}/>
          <PayRow label="Advance" value={`₹${(work.advance||0).toLocaleString()}`} T={T} color={C.success}/>
          <PayRow label="Remaining" value={`₹${((work.budget||0)-(work.advance||0)).toLocaleString()}`} T={T} color={work.paid?C.success:C.danger}/>
        </div>
      </div>

      {/* Revision */}
      <div style={s.panel}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={s.panelTitle}>🔄 Revisions ({work.revisions.length})</div>
        </div>
        {work.revisions.map((r,i)=>(
          <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{background:C.accent+"22",color:C.accent,borderRadius:20,padding:"2px 10px",fontSize:12,fontWeight:800,flexShrink:0}}>Rev {i+1}</div>
            <div style={{color:T.text,fontSize:13,flex:1}}>{r.note}</div>
            <div style={{color:T.muted,fontSize:11,flexShrink:0}}>{fmt(r.created_at)}</div>
          </div>
        ))}
        <div style={{display:"flex",gap:8,marginTop:12}}>
          <input style={{...s.input,flex:1}} placeholder="Revision note (e.g. Logo bada karo)…"
            value={revNote} onChange={e=>setRevNote(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&revNote.trim()){onRevision(revNote.trim());setRevNote("");}}} />
          <button style={s.primaryBtn} onClick={()=>{if(revNote.trim()){onRevision(revNote.trim());setRevNote("");}}}>+ Add</button>
        </div>
      </div>

      {/* Daily Progress */}
      <div style={s.panel}>
        <div style={s.panelTitle}>📈 Daily Progress</div>
        {work.progress.length===0&&<div style={{color:T.muted,fontSize:13,marginBottom:10}}>No progress yet. Add today's update!</div>}
        {work.progress.map((p,i)=>(
          <div key={p.id||i} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:`1px solid ${T.border}`,alignItems:"center"}}>
            <span style={{color:C.accent,fontSize:11,flexShrink:0}}>{fmt(p.created_at)}</span>
            <span style={{color:T.text,fontSize:13}}>· {p.line}</span>
          </div>
        ))}
        <div style={{display:"flex",gap:8,marginTop:12}}>
          <input style={{...s.input,flex:1}} placeholder="Today's update (e.g. 2 reels done)…"
            value={progLine} onChange={e=>setProgLine(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&progLine.trim()){onProgress(progLine.trim());setProgLine("");}}} />
          <button style={s.primaryBtn} onClick={()=>{if(progLine.trim()){onProgress(progLine.trim());setProgLine("");}}}>Save</button>
        </div>
      </div>

      {/* Notes */}
      <div style={s.panel}>
        <div style={s.panelTitle}>📝 Notes</div>
        <textarea style={{...s.input,height:90,resize:"vertical"}} value={noteText}
          onChange={e=>handleNoteChange(e.target.value)} placeholder="Client notes, reminders, next steps…" />
        <div style={{color:T.muted,fontSize:11,marginTop:4}}>Auto-saves as you type</div>
      </div>

      {/* Timeline */}
      <div style={s.panel}>
        <div style={s.panelTitle}>🕐 Timeline</div>
        <div style={{position:"relative",paddingLeft:20}}>
          <div style={{position:"absolute",left:7,top:0,bottom:0,width:2,background:T.border}}/>
          {work.timeline.map((t,i)=>(
            <div key={i} style={{display:"flex",gap:12,paddingBottom:12,position:"relative"}}>
              <div style={{position:"absolute",left:-20,top:4,width:10,height:10,borderRadius:"50%",background:C.accent,flexShrink:0}}/>
              <span style={{color:C.accent,fontSize:11,minWidth:110,flexShrink:0}}>{fmt(t.created_at)}</span>
              <span style={{color:T.text,fontSize:13,fontWeight:600}}>{t.event}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Description */}
      {work.description&&(
        <div style={s.panel}>
          <div style={s.panelTitle}>Description</div>
          <div style={{color:T.muted,fontSize:14}}>{work.description}</div>
        </div>
      )}
    </div>
  );
}

// ── Report Modal ──────────────────────────────────────────────────────────────
function ReportModal({ T, s, client, works, onClose }) {
  const total = works.reduce((a,w)=>a+(w.budget||0),0);
  const received = works.reduce((a,w)=>a+(w.paid?(w.budget||0):w.advance_received?(w.advance||0):0),0);
  const pending = total-received;

  const printReport = () => {
    const html = `
      <html><head><title>Report - ${client.name}</title>
      <style>body{font-family:sans-serif;padding:24px;color:#111}h1{color:#6C63FF}table{width:100%;border-collapse:collapse}td,th{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f4f5fb}.badge{display:inline-block;padding:2px 8px;border-radius:10px;font-size:12px}.summary{display:flex;gap:24px;margin:16px 0}.box{background:#f4f5fb;padding:12px 20px;border-radius:8px}</style>
      </head><body>
      <h1>◈ Workflow Report</h1>
      <h2>${client.name}${client.company?` — ${client.company}`:""}</h2>
      <p>${client.phone||""} ${client.email||""}</p>
      <div class="summary">
        <div class="box"><b>Total Budget</b><br/>₹${total.toLocaleString()}</div>
        <div class="box"><b>Received</b><br/>₹${received.toLocaleString()}</div>
        <div class="box"><b>Pending</b><br/>₹${pending.toLocaleString()}</div>
      </div>
      <table>
        <tr><th>Work</th><th>Category</th><th>Status</th><th>Revisions</th><th>Delivered</th><th>Budget</th><th>Payment</th></tr>
        ${works.map(w=>`<tr>
          <td>${w.title}</td><td>${w.category||""}</td><td>${w.status}</td>
          <td>${w.revisions.length}</td><td>${w.delivered?"Yes":"No"}</td>
          <td>₹${(w.budget||0).toLocaleString()}</td>
          <td>${w.paid?"Full Paid":w.advance_received?"Advance Received":"Pending"}</td>
        </tr>`).join("")}
      </table>
      <p style="margin-top:24px;color:#999;font-size:12px">Generated by Workflow on ${new Date().toLocaleString("en-IN")}</p>
      </body></html>
    `;
    const win = window.open("","_blank");
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const whatsappShare = () => {
    const msg = `*Workflow Report*\n*Client:* ${client.name}\n*Total Works:* ${works.length}\n*Budget:* ₹${total.toLocaleString()}\n*Received:* ₹${received.toLocaleString()}\n*Pending:* ₹${pending.toLocaleString()}\n\n${works.map(w=>`• ${w.title} — ${w.status}`).join("\n")}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,"_blank");
  };

  return (
    <div style={s.overlay}>
      <div style={{...s.modal,maxWidth:520}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{color:T.text,fontWeight:800,fontSize:16}}>📄 Report — {client.name}</div>
          <button style={s.iconBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
          {[["Total Budget",`₹${total.toLocaleString()}`,C.accent],["Received",`₹${received.toLocaleString()}`,C.success],["Pending",`₹${pending.toLocaleString()}`,C.danger]].map(([l,v,c])=>(
            <div key={l} style={{background:T.card,borderRadius:10,padding:"12px",textAlign:"center",border:`1px solid ${T.border}`}}>
              <div style={{color:c,fontWeight:800,fontSize:18}}>{v}</div>
              <div style={{color:T.muted,fontSize:11}}>{l}</div>
            </div>
          ))}
        </div>
        {works.map(w=>(
          <div key={w.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
            <div>
              <div style={{color:T.text,fontWeight:600,fontSize:13}}>{w.title}</div>
              <div style={{color:T.muted,fontSize:11}}>{w.revisions.length} revisions · {w.delivered?"Delivered":"Not delivered"}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <StatusBadge status={w.status}/>
              <div style={{color:w.paid?C.success:C.danger,fontSize:11,marginTop:4}}>{w.paid?"Paid":w.advance_received?"Advance":""}</div>
            </div>
          </div>
        ))}
        <div style={{display:"flex",gap:10,marginTop:16}}>
          <button style={{...s.primaryBtn,flex:1,background:"#25D366"}} onClick={whatsappShare}>📤 WhatsApp Share</button>
          <button style={{...s.primaryBtn,flex:1}} onClick={printReport}>🖨️ Print / PDF</button>
          <button style={{...s.ghostBtn}} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Add Client Modal ──────────────────────────────────────────────────────────
function AddClientModal({ T, s, onSave, onClose }) {
  const [f, setF] = useState({name:"",phone:"",email:"",company:"",notes:""});
  const set = k => e => setF(p=>({...p,[k]:e.target.value}));
  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <ModalHeader title="👤 Add Client" onClose={onClose} T={T}/>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:14}}>
          <input style={s.input} placeholder="Client Name *" value={f.name} onChange={set("name")}/>
          <input style={s.input} placeholder="Phone" value={f.phone} onChange={set("phone")}/>
          <input style={s.input} placeholder="Email" value={f.email} onChange={set("email")}/>
          <input style={s.input} placeholder="Company (optional)" value={f.company} onChange={set("company")}/>
          <textarea style={{...s.input,height:70}} placeholder="Notes" value={f.notes} onChange={set("notes")}/>
        </div>
        <div style={{display:"flex",gap:10,marginTop:16}}>
          <button style={{...s.primaryBtn,flex:1}} onClick={()=>{if(f.name.trim())onSave(f);}}>Save Client</button>
          <button style={{...s.ghostBtn,flex:1}} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Add Work Modal ────────────────────────────────────────────────────────────
function AddWorkModal({ T, s, clientId, onSave, onClose }) {
  const [f, setF] = useState({client_id:clientId,title:"",category:"Reel",description:"",deadline:"",priority:"Medium",tags:[],budget:"",advance:""});
  const set = k => e => setF(p=>({...p,[k]:e.target.value}));
  const toggleTag = t => setF(p=>({...p,tags:p.tags.includes(t)?p.tags.filter(x=>x!==t):[...p.tags,t]}));
  return (
    <div style={s.overlay}>
      <div style={{...s.modal,maxWidth:480}}>
        <ModalHeader title="🎯 Add Work" onClose={onClose} T={T}/>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:14,maxHeight:"65vh",overflowY:"auto"}}>
          <input style={s.input} placeholder="Work Title *" value={f.title} onChange={set("title")}/>
          <select style={s.input} value={f.category} onChange={set("category")}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
          <textarea style={{...s.input,height:60}} placeholder="Description (optional)" value={f.description} onChange={set("description")}/>
          <input style={s.input} type="date" value={f.deadline} onChange={set("deadline")}/>
          <div style={{color:T.muted,fontSize:12,marginBottom:2}}>Priority</div>
          <div style={{display:"flex",gap:8}}>
            {PRIORITIES.map(p=>(
              <button key={p} style={{...s.chipBtn,flex:1,...(f.priority===p?{background:PRIORITY_COLOR[p],color:"#fff",border:"none"}:{})}}
                onClick={()=>setF(prev=>({...prev,priority:p}))}>
                {"⭐".repeat(PRIORITIES.indexOf(p)+1)} {p}
              </button>
            ))}
          </div>
          <div style={{color:T.muted,fontSize:12,marginBottom:2}}>Tags</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {TAGS.map(t=>(
              <button key={t} style={{...s.chipBtn,...(f.tags.includes(t)?{background:C.accent,color:"#fff",border:"none"}:{})}}
                onClick={()=>toggleTag(t)}>{t}</button>
            ))}
          </div>
          <div style={{display:"flex",gap:8}}>
            <input style={s.input} type="number" placeholder="Budget ₹" value={f.budget} onChange={set("budget")}/>
            <input style={s.input} type="number" placeholder="Advance ₹" value={f.advance} onChange={set("advance")}/>
          </div>
        </div>
        <div style={{display:"flex",gap:10,marginTop:16}}>
          <button style={{...s.primaryBtn,flex:1}} onClick={()=>{if(f.title.trim())onSave(f);}}>Create Work</button>
          <button style={{...s.ghostBtn,flex:1}} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Small Components ──────────────────────────────────────────────────────────
const Avatar = ({name,size=44}) => (
  <div style={{width:size,height:size,borderRadius:"50%",background:C.accent+"33",color:C.accent,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:size*0.4,flexShrink:0}}>
    {name?.[0]?.toUpperCase()||"?"}
  </div>
);

const StatusDot = ({status}) => (
  <div style={{width:10,height:10,borderRadius:"50%",background:STATUS_COLOR[status]||"#6B7280",flexShrink:0}}/>
);

const StatusBadge = ({status}) => {
  const color = STATUS_COLOR[status]||"#6B7280";
  return <span style={{background:color+"22",color,border:`1px solid ${color}55`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{status}</span>;
};

const PriorityBadge = ({priority,large}) => (
  <span style={{background:PRIORITY_COLOR[priority]+"22",color:PRIORITY_COLOR[priority],border:`1px solid ${PRIORITY_COLOR[priority]}44`,borderRadius:20,padding:large?"5px 14px":"3px 10px",fontSize:large?13:11,fontWeight:700}}>
    {"⭐".repeat(PRIORITIES.indexOf(priority)+1)} {priority}
  </span>
);

const PayRow = ({label,value,T,color}) => (
  <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${T.border}`}}>
    <span style={{color:T.muted,fontSize:13}}>{label}</span>
    <span style={{color:color||T.text,fontWeight:700,fontSize:14}}>{value}</span>
  </div>
);

const EmptyState = ({icon,msg,small}) => (
  <div style={{textAlign:"center",padding:small?"12px":"32px",color:"#6B7280"}}>
    <div style={{fontSize:small?24:36,marginBottom:8}}>{icon}</div>
    <div style={{fontSize:small?12:14}}>{msg}</div>
  </div>
);

const Toast = ({msg,type}) => (
  <div style={{position:"fixed",bottom:24,right:24,background:type==="success"?C.success:C.danger,color:"#fff",padding:"12px 20px",borderRadius:12,fontWeight:700,fontSize:13,zIndex:9999,boxShadow:"0 4px 24px #0006",display:"flex",alignItems:"center",gap:8}}>
    {type==="success"?"✓":"✕"} {msg}
  </div>
);

const Loader = () => (
  <div style={{minHeight:"100vh",background:DARK.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{width:48,height:48,border:`4px solid ${C.accent}33`,borderTop:`4px solid ${C.accent}`,borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
    <div style={{color:DARK.muted,fontSize:14}}>Loading Workflow…</div>
  </div>
);

const ModalHeader = ({title,onClose,T}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
    <div style={{color:T.text,fontWeight:800,fontSize:16}}>{title}</div>
    <button style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:T.muted}} onClick={onClose}>✕</button>
  </div>
);

// ── Styles ────────────────────────────────────────────────────────────────────
const mkStyles = T => ({
  shell:      { minHeight:"100vh", background:T.bg, fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", color:T.text },
  header:     { background:T.surface, borderBottom:`1px solid ${T.border}`, padding:"12px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:100, backdropFilter:"blur(10px)" },
  logo:       { fontWeight:900, fontSize:18, color:C.accent, letterSpacing:"-0.5px" },
  backBtn:    { background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:8, padding:"5px 14px", cursor:"pointer", fontSize:13, fontWeight:600 },
  searchWrap: { position:"relative" },
  searchInput:{ background:T.input, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, padding:"8px 12px 8px 32px", fontSize:13, outline:"none", width:220 },
  iconBtn:    { background:"none", border:"none", cursor:"pointer", fontSize:18, padding:6 },
  nav:        { background:T.surface, borderBottom:`1px solid ${T.border}`, padding:"0 20px", display:"flex", gap:4 },
  navBtn:     { background:"none", border:"none", color:T.muted, padding:"12px 16px", cursor:"pointer", fontSize:14, fontWeight:600, borderBottom:"3px solid transparent", transition:"all 0.15s" },
  navActive:  { color:C.accent, borderBottom:`3px solid ${C.accent}` },
  main:       { padding:"24px 20px", maxWidth:860, margin:"0 auto" },
  pageTitle:  { color:T.text, fontWeight:900, fontSize:22, margin:"0 0 20px", letterSpacing:"-0.3px" },
  statsGrid:  { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:16 },
  grid2:      { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 },
  panel:      { background:T.surface, borderRadius:14, padding:18, border:`1px solid ${T.border}` },
  panelTitle: { color:T.text, fontWeight:700, fontSize:13, marginBottom:12, textTransform:"uppercase", letterSpacing:"0.06em" },
  listCard:   { background:T.surface, borderRadius:12, padding:"14px 16px", marginBottom:10, cursor:"pointer", display:"flex", alignItems:"center", gap:12, border:`1px solid ${T.border}`, transition:"border-color 0.15s" },
  clientCard: { background:T.surface, borderRadius:14, padding:"14px 18px", marginBottom:10, cursor:"pointer", display:"flex", alignItems:"center", gap:14, border:`1px solid ${T.border}` },
  tag:        { background:C.accent+"22", color:C.accent, border:`1px solid ${C.accent}44`, borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:600 },
  input:      { background:T.input, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, padding:"10px 13px", fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", fontFamily:"inherit" },
  primaryBtn: { background:C.accent, color:"#fff", border:"none", borderRadius:10, padding:"10px 20px", fontWeight:700, cursor:"pointer", fontSize:13, whiteSpace:"nowrap" },
  ghostBtn:   { background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:10, padding:"10px 16px", fontWeight:600, cursor:"pointer", fontSize:13 },
  toggleBtn:  { borderRadius:10, padding:"12px", fontWeight:700, cursor:"pointer", fontSize:13, textAlign:"center", transition:"all 0.2s" },
  chipBtn:    { background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:20, padding:"6px 14px", fontSize:12, cursor:"pointer" },
  overlay:    { position:"fixed", inset:0, background:"#00000090", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:20, backdropFilter:"blur(4px)" },
  modal:      { background:T.surface, borderRadius:18, padding:24, width:"100%", maxWidth:440, maxHeight:"90vh", overflowY:"auto", border:`1px solid ${T.border}`, boxShadow:"0 20px 60px #0008" },
});
